import {
  ACCOUNT_MERGE_PREVIEW_LOADING,
  ACCOUNT_MERGE_PREVIEW_SUCCESS,
  ACCOUNT_MERGE_PREVIEW_FAILURE,
  ACCOUNT_MERGE_SWAP_LOADING,
  ACCOUNT_MERGE_SWAP_SUCCESS,
  ACCOUNT_MERGE_SWAP_FAILURE,
  ACCOUNT_MERGE_SWAP_RESET,
  ACCOUNT_MERGE_EXECUTING,
  ACCOUNT_MERGE_SUCCESS,
  ACCOUNT_MERGE_FAILURE,
  ACCOUNT_MERGE_RESET,
  ACCOUNT_MERGE_VERIFYING,
  ACCOUNT_MERGE_VERIFY_SUCCESS,
  ACCOUNT_MERGE_VERIFY_FAILURE,
  ACCOUNT_MERGE_VERIFY_TIMEOUT
} from "./actionTypes";
import { displayToastMessage } from "./statusActions";
import {
  getAccountMergePreview,
  executeAccountMerge,
  verifyMergeStatus
} from "../api/bff";
import { reportToGTM } from "../api/gtm-analytics";
import i18n from "../i18n";
import appConfig from "../config/appConfig";
import { MESSAGE_TYPE, MESSAGE_SEVERITY } from "../utils/MessageTypes";
import { mergeSuccessQueryParam } from "../utils/stringConstants";

// Verification polling configuration
// Budget: 45 retries × 2 s = 90 s, matching the session-invalidation
// eventual-consistency window (user data-plane + async solution eventing).
export const VERIFY_POLL_INTERVAL_MS = 2000;
export const VERIFY_MAX_RETRIES = 45;

/**
 * Returns a promise that resolves after the specified delay.
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>} Resolves after delay
 * @visibleForTesting Exported for jest.useFakeTimers() in tests
 */
export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Action creator for loading state
 */
function mergePreviewLoading() {
  return { type: ACCOUNT_MERGE_PREVIEW_LOADING };
}

/**
 * Action creator for successful preview fetch
 * @param {Object} previewData - The merge preview data from BFF
 * @param {string} authCode - The auth code for later use in merge execution
 */
function mergePreviewSuccess(previewData, authCode) {
  return {
    type: ACCOUNT_MERGE_PREVIEW_SUCCESS,
    payload: { previewData, authCode }
  };
}

/**
 * Action creator for preview fetch failure
 * @param {string} error - Error message
 */
function mergePreviewFailure(error) {
  return { type: ACCOUNT_MERGE_PREVIEW_FAILURE, payload: { error } };
}

/**
 * Action creator for merge execution in progress
 */
function mergeExecuting() {
  return { type: ACCOUNT_MERGE_EXECUTING };
}

/**
 * Action creator for successful merge
 */
function mergeSuccess() {
  return { type: ACCOUNT_MERGE_SUCCESS };
}

/**
 * Action creator for merge failure
 * @param {string} error - Error message
 */
function mergeFailure(error) {
  return { type: ACCOUNT_MERGE_FAILURE, payload: { error } };
}

/**
 * Action creator for verification in progress
 */
function mergeVerifying() {
  return { type: ACCOUNT_MERGE_VERIFYING };
}

/**
 * Action creator for successful verification
 */
function mergeVerifySuccess() {
  return { type: ACCOUNT_MERGE_VERIFY_SUCCESS };
}

/**
 * Action creator for verification failure
 * @param {string} error - Error message
 */
function mergeVerifyFailure(error) {
  return { type: ACCOUNT_MERGE_VERIFY_FAILURE, payload: { error } };
}

/**
 * Action creator for verification timeout
 * @param {string} error - Error message
 */
function mergeVerifyTimeout(error) {
  return { type: ACCOUNT_MERGE_VERIFY_TIMEOUT, payload: { error } };
}

/**
 * Action creator to reset merge state
 */
export function resetAccountMerge() {
  return { type: ACCOUNT_MERGE_RESET };
}

/**
 * Thunk action to reset swap state (toggle back to original direction)
 */
export function resetSwap() {
  return function (dispatch) {
    dispatch({ type: ACCOUNT_MERGE_SWAP_RESET });

    reportToGTM(
      "Account Merge Swap",
      "Reset",
      "User toggled back to original merge direction",
      "Account Merge"
    );
  };
}

/**
 * Action creator for swap eligibility loading
 */
function swapEligibilityLoading() {
  return { type: ACCOUNT_MERGE_SWAP_LOADING };
}

/**
 * Action creator for swap eligibility success
 * @param {Object} swappedEligibility - The eligibility data for swapped direction
 */
function swapEligibilitySuccess(swappedEligibility) {
  return { type: ACCOUNT_MERGE_SWAP_SUCCESS, payload: { swappedEligibility } };
}

/**
 * Action creator for swap eligibility failure
 * @param {string} error - Error message
 */
function swapEligibilityFailure(error) {
  return { type: ACCOUNT_MERGE_SWAP_FAILURE, payload: { error } };
}

/**
 * Thunk action to display the merge success toast message.
 * This is called from HomePage when detecting the mergeSuccess query parameter.
 */
export function displayMergeSuccessMessage() {
  return async function (dispatch) {
    dispatch(
      displayToastMessage({
        text: i18n.t(
          "merge.success",
          "Your accounts have been successfully merged."
        ),
        type: MESSAGE_TYPE.TOAST,
        severity: MESSAGE_SEVERITY.SUCCESS
      })
    );
  };
}

/**
 * Transforms a User API user object to the UI account format
 * @param {Object} user - User object from BFF (User API format)
 * @returns {Object} - Account object for UI consumption
 */
function transformUserToAccount(user) {
  return {
    displayName: `${user.profile?.givenName || ""} ${
      user.profile?.familyName || ""
    }`.trim(),
    username: user.identity?.username || user.profile?.preferredUsername || "",
    firstName: user.profile?.givenName || "",
    lastName: user.profile?.familyName || "",
    email: user.profile?.primaryEmail || "",
    solutionLinks: user.solutionLinks || []
  };
}

/**
 * Transforms BFF preview response to UI format
 * @param {Object} bffResponse - Response from BFF /account-merge/preview
 * @returns {Object} - Transformed data for MergeSummaryPage
 */
function transformPreviewResponse(bffResponse) {
  // currentUser = the logged-in user initiating the merge (primary account)
  // otherUser = the account they're merging with (second account)
  const primaryAccount = transformUserToAccount(bffResponse.currentUser);
  const secondAccount = transformUserToAccount(bffResponse.otherUser);

  // Combined account starts with primary account, then overrides with result fields
  const combinedAccount = {
    ...primaryAccount,
    ...(bffResponse.result?.solutionLinks && {
      solutionLinks: bffResponse.result.solutionLinks
    })
  };

  return {
    primaryAccount,
    secondAccount,
    combinedAccount,
    isEligible: bffResponse.isEligible,
    conflicts: bffResponse.conflicts || [],
    expiresAt: bffResponse.expiresAt
  };
}

/**
 * Thunk action to fetch merge preview data from BFF
 * Called when user returns from SignIn OAuth flow with auth code
 * @param {string} authCode - The authorization code from SignIn
 */
export function fetchMergePreview(authCode) {
  return async function (dispatch) {
    if (!authCode) {
      const errorMessage = i18n.t(
        "merge.errors.no-auth-code",
        "Missing authorization code."
      );
      dispatch(mergePreviewFailure(errorMessage));
      dispatch(
        displayToastMessage({
          text: errorMessage,
          type: MESSAGE_TYPE.TOAST,
          severity: MESSAGE_SEVERITY.FAIL
        })
      );
      return;
    }

    dispatch(mergePreviewLoading());

    try {
      // Get the redirect URI from app config to pass to BFF
      const redirectUri = appConfig.signin.accountMergeRedirectURL;
      const response = await getAccountMergePreview(authCode, redirectUri);

      // Transform BFF response to UI format
      const transformedData = transformPreviewResponse(response.data);

      dispatch(mergePreviewSuccess(transformedData, authCode));

      reportToGTM(
        "Account Merge Preview",
        "Loaded",
        "Preview data loaded successfully",
        "Account Merge"
      );
    } catch (error) {
      console.error("Failed to get merge preview:", error);

      const errorMessage =
        error.response?.data?.message ||
        i18n.t(
          "merge.errors.preview-failed",
          "Failed to load merge preview. Please try again."
        );

      dispatch(mergePreviewFailure(errorMessage));
      dispatch(
        displayToastMessage({
          text: errorMessage,
          type: MESSAGE_TYPE.TOAST,
          severity: MESSAGE_SEVERITY.FAIL
        })
      );

      reportToGTM(
        "Account Merge Preview",
        "Error",
        error.message || "Failed to load preview",
        "Account Merge"
      );
    }
  };
}

/**
 * Thunk action to fetch eligibility for the swapped direction.
 * Called when user toggles the swap accounts button.
 * Caches the result so we only fetch once.
 */
export function fetchSwappedEligibility() {
  return async function (dispatch, getState) {
    const { accountMergeReducer } = getState();
    const authCode = accountMergeReducer.authCode;

    // Prevent duplicate requests if already loading
    if (accountMergeReducer.isLoadingSwap) {
      return;
    }

    // If we already have swapped eligibility data, use cached result
    // This function is only called when isSwapped is false (from handleSwap in MergeSummaryPage)
    if (accountMergeReducer.swappedEligibility !== null) {
      dispatch(swapEligibilitySuccess(accountMergeReducer.swappedEligibility));
      return;
    }

    if (!authCode) {
      console.error("No auth code available for swap eligibility check");
      return;
    }

    dispatch(swapEligibilityLoading());

    try {
      const redirectUri = appConfig.signin.accountMergeRedirectURL;
      // Call preview with swapAccounts=true to get eligibility for swapped direction
      const response = await getAccountMergePreview(
        authCode,
        redirectUri,
        true
      );

      dispatch(
        swapEligibilitySuccess({
          isEligible: response.data.isEligible,
          conflicts: response.data.conflicts || []
        })
      );

      reportToGTM(
        "Account Merge Swap",
        "Eligibility Checked",
        response.data.isEligible
          ? "Swap direction eligible"
          : "Swap direction blocked",
        "Account Merge"
      );
    } catch (error) {
      console.error("Failed to fetch swapped eligibility:", error);

      const errorMessage =
        error.response?.data?.message ||
        i18n.t(
          "merge.errors.swap-check-failed",
          "Failed to check swap eligibility."
        );

      dispatch(swapEligibilityFailure(errorMessage));
      dispatch(
        displayToastMessage({
          text: errorMessage,
          type: MESSAGE_TYPE.TOAST,
          severity: MESSAGE_SEVERITY.FAIL
        })
      );

      reportToGTM(
        "Account Merge Swap",
        "Error",
        error.message || "Failed to check swap eligibility",
        "Account Merge"
      );
    }
  };
}

/**
 * Thunk action to execute the account merge
 * @param {boolean} swapAccounts - If true, swap surviving/non-surviving accounts
 */
export function performAccountMerge(swapAccounts = false) {
  return async function (dispatch, getState) {
    const { accountMergeReducer } = getState();
    const authCode = accountMergeReducer.authCode;

    // Prevent merge execution while swap eligibility is still loading
    if (accountMergeReducer.isLoadingSwap) {
      console.warn("Cannot execute merge while swap eligibility is loading");
      return;
    }

    // Prevent duplicate merge execution (e.g. double-click before UI disables button)
    if (accountMergeReducer.isExecutingMerge) {
      return;
    }

    if (!authCode) {
      const errorMessage = i18n.t(
        "merge.errors.no-auth-code",
        "Missing authorization code."
      );
      dispatch(mergeFailure(errorMessage));
      dispatch(
        displayToastMessage({
          text: errorMessage,
          type: MESSAGE_TYPE.TOAST,
          severity: MESSAGE_SEVERITY.FAIL
        })
      );
      return;
    }

    dispatch(mergeExecuting());

    try {
      const redirectUri = appConfig.signin.accountMergeRedirectURL;
      const mergeResponse = await executeAccountMerge(
        authCode,
        redirectUri,
        swapAccounts
      );

      // Build the expected result for verification from merge response
      const expected = mergeResponse?.data || null;

      // Dispatch verifying BEFORE success so the loading indicator is up
      // before showMergePage flips to false. React 17 does not batch
      // dispatches in async thunk context, so reversing these would cause
      // a render frame where no loading flag is true (UI flash).
      dispatch(mergeVerifying());
      dispatch(mergeSuccess());

      reportToGTM(
        "Account Merge",
        "Completed",
        swapAccounts
          ? "Accounts merged with swap"
          : "Accounts merged successfully",
        "Account Merge"
      );

      let verified = false;

      if (expected) {
        let lastError = null;

        for (let attempt = 0; attempt < VERIFY_MAX_RETRIES; attempt++) {
          await delay(VERIFY_POLL_INTERVAL_MS);

          try {
            const verifyResponse = await verifyMergeStatus(
              authCode,
              swapAccounts,
              expected
            );

            lastError = null; // Clear any previous transient error

            if (verifyResponse.data?.inSync) {
              verified = true;
              dispatch(mergeVerifySuccess());

              reportToGTM(
                "Account Merge Verification",
                "Verified",
                `Merge verified after ${attempt + 1} attempt(s)`,
                "Account Merge"
              );

              break;
            }
          } catch (verifyError) {
            lastError = verifyError;
            console.error(
              `Merge verification attempt ${attempt + 1} failed:`,
              verifyError
            );
            // Continue retrying — transient errors should not abort the loop
          }
        }

        if (!verified && lastError) {
          // All retries exhausted and last attempt was an error
          const verifyErrorMessage = i18n.t(
            "merge.errors.verify-failed",
            "We could not confirm merge completion at this time. Your merge was submitted successfully. Please contact your dealer administrator if your solutions are not updated shortly."
          );
          dispatch(mergeVerifyFailure(verifyErrorMessage));
          dispatch(
            displayToastMessage({
              text: verifyErrorMessage,
              type: MESSAGE_TYPE.TOAST,
              severity: MESSAGE_SEVERITY.WARNING
            })
          );

          reportToGTM(
            "Account Merge Verification",
            "Error",
            lastError.message || "Verification request failed",
            "Account Merge"
          );
        } else if (!verified) {
          // Timed out — merge was submitted, but verification didn't confirm
          const timeoutMessage = i18n.t(
            "merge.errors.verify-timeout",
            "Verification is taking longer than expected. Your merge was submitted successfully. Please contact your dealer administrator if your solutions are not updated shortly."
          );
          dispatch(mergeVerifyTimeout(timeoutMessage));
          dispatch(
            displayToastMessage({
              text: timeoutMessage,
              type: MESSAGE_TYPE.TOAST,
              severity: MESSAGE_SEVERITY.WARNING
            })
          );

          reportToGTM(
            "Account Merge Verification",
            "Timeout",
            `Verification timed out after ${VERIFY_MAX_RETRIES} attempts`,
            "Account Merge"
          );
        }
      } else {
        // No merge response data to verify against — skip verification
        dispatch(mergeVerifySuccess());
      }

      // Handle post-merge redirect regardless of verification outcome.
      // The merge itself succeeded; verification is best-effort.
      // Wrapped in its own try-catch so a redirect/URL error does NOT dispatch
      // mergeFailure — the merge already succeeded server-side and is irreversible.
      try {
        if (swapAccounts) {
          // Merge complete, but current session is now invalid (account deactivated)
          // Redirect to SignIn logout endpoint with bridge-bar params and next redirect
          const isProd = appConfig.partition === "prod";
          const baseURL = appConfig.signin.baseURL;
          const bridgeSolution = appConfig.signin.bridgeSolution || "CRS";
          // Include mergeSuccess param in the next URL so user sees success message after re-login
          const homeUrl = new URL(appConfig.auth.logoutUrl);
          homeUrl.searchParams.set(mergeSuccessQueryParam, "yes");
          const nextUrl = encodeURIComponent(homeUrl.toString());

          // Include 'next' param to redirect back after logout
          // Prod: bridge_solution + next params
          // Non-prod: bridge_env=QA + bridge_solution + next params
          const signinLogoutUrl = isProd
            ? `${baseURL}/logout?bridge_solution=${bridgeSolution}&next=${nextUrl}`
            : `${baseURL}/logout?bridge_env=QA&bridge_solution=${bridgeSolution}&next=${nextUrl}`;

          window.location.href = signinLogoutUrl;
        } else {
          // Merge complete, current session still valid
          // Redirect to home page with success query param
          window.location.assign(
            "/?partition=" +
              appConfig.partition +
              "&" +
              mergeSuccessQueryParam +
              "=yes"
          );
        }
      } catch (redirectError) {
        // Merge succeeded but redirect failed — log and fall back to simple reload
        console.error("Post-merge redirect failed:", redirectError);
        window.location.assign("/?partition=" + appConfig.partition);
      }
    } catch (error) {
      console.error("Failed to execute merge:", error);

      const errorMessage =
        error.response?.data?.message ||
        i18n.t(
          "merge.errors.merge-failed",
          "Failed to merge accounts. Please try again."
        );

      dispatch(mergeFailure(errorMessage));
      dispatch(
        displayToastMessage({
          text: errorMessage,
          type: MESSAGE_TYPE.TOAST,
          severity: MESSAGE_SEVERITY.FAIL
        })
      );

      reportToGTM(
        "Account Merge",
        "Error",
        error.message || "Failed to merge accounts",
        "Account Merge"
      );
    }
  };
}

/**
 * Thunk action to cancel the merge flow
 */
export function cancelAccountMerge() {
  return function (dispatch) {
    dispatch(resetAccountMerge());

    reportToGTM(
      "Account Merge",
      "Cancelled",
      "User cancelled merge flow",
      "Account Merge"
    );
  };
}
